/**
 * The client portal: two screens, a different shell, and deliberately quieter.
 *
 * Read off landing/src/components/surfaces/PortalCard.astro. No night rail, no KPI strip,
 * no jargon — a plain-language sentence, a per-authority list, and one primary action. The
 * type is larger than the office app's because clients use this rarely and on phones.
 *
 * The rule enforced here as well as in the API: nothing on this screen can show
 * profitability, an engagement score or firm workload. The portal endpoints do not return
 * those fields at all, so there is nothing to render even by accident.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatMonthName } from "@onecpa/shared";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FirmMark,
  Panel,
  Segmented,
  Spinner,
  StatusBadge,
  Textarea,
  ToastStack,
  Wordmark,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthProvider.js";
import { useSession } from "../app/session.js";

type Screen = "workspace" | "history";

export function Portal() {
  const session = useSession();
  const { signOutNow } = useAuth();
  const queryClient = useQueryClient();
  const [screen, setScreen] = useState<Screen>("workspace");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["portal", session.orgId],
    queryFn: () => api.portal.me(session.orgId),
  });

  const history = useQuery({
    queryKey: ["portal", "history", session.orgId],
    queryFn: () => api.portal.history(session.orgId),
    enabled: screen === "history",
  });

  const confirm = useMutation({
    mutationFn: () => api.portal.confirmMonth(session.orgId, note.trim() || undefined),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["portal"] });
      session.toast(
        result.confirmed > 0 ? "תודה, העברת החומר נרשמה והמשרד עודכן" : "כבר אישרתם את החודש הזה",
      );
      setNote("");
      setConfirming(false);
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  return (
    <div className="min-h-dvh bg-paper">
      {/* An ink top bar, as on the phone composition — but no rail: the portal is not an
          application the client works inside all day.

          The firm leads, and OneCPA is demoted to a credit at the end. That is the
          honest hierarchy on a client-facing surface: the client engaged an
          accountancy practice, not a software vendor, and "who is handling my
          affairs" should be answerable at a glance rather than inferred from a
          line of small grey text. */}
      <header className="flex items-center gap-3 bg-night px-4 py-3">
        <span className="flex min-w-0 items-center gap-2.5">
          {session.org.logoUrl ? (
            <img
              src={session.org.logoUrl}
              alt={session.orgName}
              className="h-9 w-auto max-w-[190px] object-contain object-right"
            />
          ) : (
            <>
              <FirmMark
                firm={{
                  name: session.orgName,
                  logoUrl: null,
                  monogram: session.org.monogram,
                }}
                variant="tile"
              />
              <span className="truncate text-base font-semibold text-on-ink">
                {session.orgName}
              </span>
            </>
          )}
        </span>

        <button
          type="button"
          onClick={() => void signOutNow()}
          className="ms-auto text-xs text-night-ink hover:text-on-ink"
        >
          יציאה
        </button>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {error ? (
          <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />
        ) : isLoading || !data ? (
          <div className="grid place-items-center py-20">
            <Spinner className="size-6 text-ledger" />
          </div>
        ) : (
          <>
            <h1 className="serif text-xl font-bold text-ink">שלום, {data.client.name}</h1>

            {/* Named in words as well as shown as a mark. The masthead answers this
                for anyone who recognises the logo; a client who does not — or who
                is on a phone, where it is small — should not have to work it out. */}
            <p className="mt-1 text-base text-ink-muted">
              המשרד המטפל בתיק שלך:{" "}
              <span className="font-semibold text-ink">{session.orgName}</span>
            </p>

            {/* The single plain-language sentence. Composed server-side so the portal and
                any reminder describe the same state in the same words. */}
            <p
              className={`mt-2 rounded-card border px-3.5 py-3 text-md ${
                data.headline.tone === "success"
                  ? "border-success-ink/30 bg-success-bg text-success-ink"
                  : data.headline.tone === "warning"
                    ? "border-warning-ink/30 bg-warning-bg text-warning-ink"
                    : "border-pending-ink/30 bg-pending-bg text-pending-ink"
              }`}
            >
              {data.headline.text}
            </p>

            <div className="mt-5">
              <Segmented<Screen>
                options={[
                  { value: "workspace", label: "מה נדרש ממני" },
                  { value: "history", label: "הדיווחים שלי" },
                ]}
                value={screen}
                onChange={setScreen}
              />
            </div>

            {screen === "workspace" && (
              <div className="mt-5 flex flex-col gap-4">
                <Panel
                  title={`הדיווחים לחודש ${formatMonthName(data.period.month)} ${data.period.year}`}
                >
                  {data.obligations.length > 0 ? (
                    <ul className="divide-y divide-line-soft">
                      {data.obligations.map((obligation) => {
                        const done =
                          obligation.status === "office_confirmed" ||
                          obligation.status === "client_confirmed";
                        return (
                          <li key={obligation.id} className="flex items-center gap-3 px-4 py-3">
                            <span
                              className={`flex-1 text-md ${done ? "text-ink-faint" : "text-ink"}`}
                            >
                              {obligation.authorityLabel}
                            </span>
                            <bdi className="tnum text-sm text-ink-faint">
                              עד {formatDate(obligation.dueDate)}
                            </bdi>
                            <StatusBadge vocabulary="monthly" status={obligation.status} />
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <EmptyState title="אין דיווחים לחודש זה" icon="check" />
                  )}
                </Panel>

                {data.requests.length > 0 && (
                  <Panel title="מסמכים שהמשרד מבקש">
                    <ul className="divide-y divide-line-soft">
                      {data.requests.map((requestItem) => (
                        <li key={requestItem.id} className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="flex-1 text-md text-ink">{requestItem.label}</span>
                            {requestItem.status === "requires_completion" ? (
                              <Badge tone="warning">נדרשת השלמה</Badge>
                            ) : (
                              <Badge tone="pending">ממתין</Badge>
                            )}
                          </div>
                          {requestItem.officeNote && (
                            <p className="mt-1.5 text-sm text-ink-muted">
                              {requestItem.officeNote}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Panel>
                )}

                {data.canConfirm && (
                  <Panel>
                    <div className="flex flex-col gap-3 px-4 py-4">
                      {confirming ? (
                        <>
                          <Textarea
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            placeholder="משהו שכדאי שנדע? (לא חובה)"
                            aria-label="הערה למשרד"
                            className="text-md"
                          />
                          <Button
                            variant="primary"
                            className="py-2.5 text-md"
                            loading={confirm.isPending}
                            onClick={() => confirm.mutate()}
                          >
                            אישור: העברתי את כל החומר
                          </Button>
                          <Button variant="ghost" onClick={() => setConfirming(false)}>
                            ביטול
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* The one-tap action from the landing page's portal card, with the
                              honest secondary below it rather than hidden. */}
                          <Button
                            variant="primary"
                            className="py-2.5 text-md"
                            onClick={() => setConfirming(true)}
                          >
                            העברתי את כל החומר
                          </Button>
                          <p className="text-center text-sm text-ink-faint">
                            עדיין לא העברתי — אחזור לכאן כשאשלח
                          </p>
                        </>
                      )}
                    </div>
                  </Panel>
                )}

                {data.documents.length > 0 && (
                  <Panel title="המסמכים שלי">
                    <ul className="divide-y divide-line-soft">
                      {data.documents.map((file) => (
                        <li key={file.id} className="flex items-center gap-3 px-4 py-2.5">
                          <span className="min-w-0 flex-1 truncate text-base text-ink">
                            {file.displayName}
                          </span>
                          <StatusBadge vocabulary="scan" status={file.scanStatus} />
                        </li>
                      ))}
                    </ul>
                  </Panel>
                )}

                {data.signatures.length > 0 && (
                  <Panel title="ממתין לחתימה">
                    <ul className="divide-y divide-line-soft">
                      {data.signatures.map((signature) => (
                        <li key={signature.id} className="flex items-center gap-3 px-4 py-2.5">
                          <span className="flex-1 text-base text-ink">{signature.title}</span>
                          <StatusBadge vocabulary="signature" status={signature.status} />
                        </li>
                      ))}
                    </ul>
                  </Panel>
                )}
              </div>
            )}

            {screen === "history" && (
              <Panel title="הדיווחים שלי" className="mt-5">
                {(history.data?.history ?? []).length > 0 ? (
                  <ul className="divide-y divide-line-soft">
                    {(history.data?.history ?? []).map((row) => (
                      <li key={row.id} className="flex items-center gap-3 px-4 py-2.5">
                        <span className="w-28 text-base text-ink">
                          {formatMonthName(row.periodMonth)} {row.periodYear}
                        </span>
                        <span className="flex-1 text-base text-ink-muted">
                          {row.authorityLabel}
                        </span>
                        {/* Lateness and its consequence are shown to the client on purpose:
                            hiding the penalty would make the reminders feel arbitrary. */}
                        {row.penaltyAmount && (
                          <bdi className="tnum text-sm font-semibold text-red-ink">
                            ₪{Number(row.penaltyAmount).toLocaleString("he-IL")}
                          </bdi>
                        )}
                        <StatusBadge vocabulary="monthly" status={row.status} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState title="אין היסטוריה" />
                )}
              </Panel>
            )}
          </>
        )}
      </main>

      {/* The product credit, kept but demoted out of the masthead. */}
      <footer className="mx-auto max-w-2xl px-4 pt-2 pb-8">
        <span className="flex items-center gap-2 border-t border-line-soft pt-4 text-2xs text-ink-faint">
          מופעל על ידי
          <Wordmark tone="dark" />
        </span>
      </footer>

      <ToastStack messages={session.toasts} onDismiss={session.dismissToast} />
    </div>
  );
}
